import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Download, ArrowUpDown, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';

function LinkViewsCard({ startDate, endDate }) {
  const [linkViewsData, setLinkViewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('views');

  const fetchLinkViews = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await apiService.admin.getAdminLinkViews({
        createdAt: { gte: startDate.toISOString(), lte: endDate.toISOString() },
        skip: currentPage * rowsPerPage,
        limit: rowsPerPage,
        order: order === 'asc' ? 1 : -1,
        orderBy,
      });
      setLinkViewsData(result.paginatedResult || []);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch link views:', error);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, currentPage, rowsPerPage, order, orderBy]);

  useEffect(() => {
    fetchLinkViews();
  }, [fetchLinkViews]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
    setCurrentPage(0);
  };

  const handleExport = () => {
    const exportUrl = new URL(
      '/api/v1/users-history/export/links-views',
      process.env.REACT_APP_API_URL,
    );
    exportUrl.searchParams.set('order', order === 'asc' ? 1 : -1);
    exportUrl.searchParams.set('orderBy', orderBy);
    exportUrl.searchParams.set('createdAt[gte]', startDate.toISOString());
    exportUrl.searchParams.set('createdAt[lte]', endDate.toISOString());
    window.open(exportUrl.toString(), '_blank');
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startIndex = currentPage * rowsPerPage + 1;
  const endIndex = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  return (
    <div className="space-y-4">
      {/* Header with Export */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-1" />
          Export
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Link</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                <button
                  type="button"
                  onClick={() => handleSort('views')}
                  className="flex items-center gap-1 hover:text-foreground"
                >
                  Views
                  <ArrowUpDown className={cn('h-4 w-4', orderBy === 'views' && 'text-primary')} />
                </button>
              </th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                <button
                  type="button"
                  onClick={() => handleSort('uniqueViews')}
                  className="flex items-center gap-1 hover:text-foreground"
                >
                  Unique Views
                  <ArrowUpDown
                    className={cn('h-4 w-4', orderBy === 'uniqueViews' && 'text-primary')}
                  />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }, (_, index) => (
                <tr key={index} className="border-t border-border">
                  <td className="p-3">
                    <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-12 bg-muted rounded animate-pulse" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-12 bg-muted rounded animate-pulse" />
                  </td>
                </tr>
              ))}
            {!isLoading && linkViewsData.length === 0 && (
              <tr>
                <td colSpan={3} className="p-8 text-center text-muted-foreground">
                  No link views found for the selected period
                </td>
              </tr>
            )}
            {!isLoading &&
              linkViewsData.length > 0 &&
              linkViewsData.map((row) => (
                <tr key={row._id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-3">
                    <a
                      href={row.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary hover:underline max-w-md truncate"
                    >
                      {row.link}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  </td>
                  <td className="p-3">
                    <span className="font-medium">{row.views?.toLocaleString() || 0}</span>
                  </td>
                  <td className="p-3">
                    <span className="font-medium">{row.uniqueViews?.toLocaleString() || 0}</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            className={cn(
              'px-2 py-1 rounded border border-input bg-background',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          >
            {[5, 10, 25, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="ml-4">
            {totalCount > 0 ? `${startIndex}-${endIndex} of ${totalCount}` : '0 results'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className={cn(
              'p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="px-2 text-sm">
            Page {currentPage + 1} of {totalPages || 1}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className={cn(
              'p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

LinkViewsCard.propTypes = {
  startDate: PropTypes.instanceOf(dayjs).isRequired,
  endDate: PropTypes.instanceOf(dayjs).isRequired,
};

export default LinkViewsCard;
